import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useConfirm from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';
import {
  copyTextToClipboard,
  formatAuthorsForDisplay,
  formatCitation,
  getCitationActor,
  parseAuthorName
} from '../utils/thesisCitation';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ThesisList = ({ currentUser }) => { 
  const [theses, setTheses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [departments, setDepartments] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [dept, setDept] = useState('');
  const [degree, setDegree] = useState('');
  const [major, setMajor] = useState('');
  const [sortField, setSortField] = useState('modified');
  const [sortDirection, setSortDirection] = useState('desc');
  const { confirm, confirmState, handleClose } = useConfirm();
  const [missingPdfFiles, setMissingPdfFiles] = useState({});
  const [pdfModalThesis, setPdfModalThesis] = useState(null);
  const [pdfModalUrl, setPdfModalUrl] = useState('');
  const [isPdfModalLoading, setIsPdfModalLoading] = useState(false);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [copiedCitationId, setCopiedCitationId] = useState(null);

  const getSignedUrl = async (filePath) => {
    if (!filePath) return '';
    if (/^https?:\/\//i.test(filePath)) return filePath;
    if (!currentUser?.user_id) return '';
    try {
      const res = await axios.get('/api/files/signed', {
        params: { path: filePath, userId: currentUser.user_id }
      });
      return res.data?.url || '';
    } catch (err) {
      alert(err.response?.data?.error || 'Unable to access this file.');
      return '';
    }
  };

  const getFilenameFromPath = (filePath) => {
    if (!filePath) return 'thesis.pdf';
    const withoutQuery = filePath.split('?')[0];
    const name = withoutQuery.split('/').pop() || 'thesis.pdf';
    return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
  };

  const handleDownload = async (filePath) => {
    const url = await getSignedUrl(filePath);
    if (!url) return;
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFilenameFromPath(filePath);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert('Failed to download file. Please try again.');
    }
  };

  const handleMissingPdfFileChange = (thesisId, file) => {
    setMissingPdfFiles(prev => ({ ...prev, [thesisId]: file }));
  };

  const uploadMissingPdf = async (thesis) => {
    if (!currentUser?.user_id) return;
    const file = missingPdfFiles[thesis.thesis_id];
    if (!file) {
      alert('Please select a PDF file to upload.');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      alert('Only PDF files can be uploaded.');
      return;
    }

    const ok = await confirm({
      title: 'Attach PDF to Thesis',
      message: 'Uploading a PDF will attach it to this thesis record, replace any existing file for this thesis, and make the document available for reading and download in the archive. Continue?',
      confirmText: 'Upload PDF',
      cancelText: 'Cancel'
    });
    if (!ok) return;

    const data = new FormData();
    data.append('thesis_id', thesis.thesis_id);
    data.append('title', thesis.title || '');
    data.append('year', thesis.year || '');
    data.append('department', thesis.department || '');
    data.append('abstract', thesis.abstract || '');
    if (thesis.degree_id) {
      data.append('degreeId', thesis.degree_id);
    }
    if (thesis.major_id) {
      data.append('majorId', thesis.major_id);
    }
    data.append('thesisFile', file);
    data.append('userId', currentUser.user_id);

    try {
      setIsPdfUploading(true);
      await axios.put(`/api/theses/${thesis.thesis_id}`, data);
      setMissingPdfFiles(prev => {
        const copy = { ...prev };
        delete copy[thesis.thesis_id];
        return copy;
      });
      setPdfModalThesis(null);
      setPdfModalUrl('');
      fetchData();
      alert('PDF uploaded successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to upload PDF. Please try again.');
    } finally {
      setIsPdfUploading(false);
    }
  };

  const getDegreeName = (degreeId) => {
    if (!degreeId) return '';
    for (const departmentItem of departments) {
      const match = (departmentItem.degrees || []).find((degreeItem) => String(degreeItem.degree_id) === String(degreeId));
      if (match) return match.degree_name;
    }
    return '';
  };

  const getMajorName = (majorId) => {
    if (!majorId) return '';
    for (const departmentItem of departments) {
      for (const degreeItem of departmentItem.degrees || []) {
        const match = (degreeItem.majors || []).find((majorItem) => String(majorItem.major_id) === String(majorId));
        if (match) return match.major_name;
      }
    }
    return '';
  };

  const getCitationText = (thesis) => formatCitation(thesis, {
    getDegreeName,
    getMajorName
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewThesis, setViewThesis] = useState(null); 
  const [selectedFile, setSelectedFile] = useState(null);
  const [editFormData, setEditFormData] = useState({ thesis_id: '', title: '', year: '', department: '', degree: '', abstract: '', major: '' });
  const [editAuthors, setEditAuthors] = useState([]);
  const [editKeywords, setEditKeywords] = useState([]);
  const [editDegreesForDept, setEditDegreesForDept] = useState([]);
  const [editMajorsForDegree, setEditMajorsForDegree] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);
  const [originalAuthorEmails, setOriginalAuthorEmails] = useState([]);

  const closePdfModal = () => {
    setPdfModalThesis(null);
    setPdfModalUrl('');
    setIsPdfModalLoading(false);
  };

  const getSortByParam = () => `${sortField}_${sortDirection}`;

  const fetchData = async () => {
    try {
      const res = await axios.get(`/api/theses/search`, { 
          params: {
            keyword,
            department: dept,
            sortBy: getSortByParam(),
            majorId: major,
            degreeId: degree,
            ...getCitationActor(currentUser)
          }
      });
      setTheses(res.data);
      const deptRes = await axios.get('/api/departments');
      setDepartments(deptRes.data);
    } catch (error) { console.error(error); }
  };

  const applyViewUpdate = (thesisId, viewCount) => {
    const normalizedCount = Number(viewCount || 0);
    setTheses(prev => prev.map(thesis => (
      String(thesis.thesis_id) === String(thesisId)
        ? { ...thesis, view_count: normalizedCount }
        : thesis
    )));
    setViewThesis(prev => (
      prev && String(prev.thesis_id) === String(thesisId)
        ? { ...prev, view_count: normalizedCount }
        : prev
    ));
  };

  const applyCitationUpdate = (thesisId, citationCount, hasCited = true) => {
    const normalizedCount = Number(citationCount || 0);
    setTheses(prev => prev.map(thesis => (
      String(thesis.thesis_id) === String(thesisId)
        ? { ...thesis, citation_count: normalizedCount, has_cited: hasCited }
        : thesis
    )));
    setViewThesis(prev => (
      prev && String(prev.thesis_id) === String(thesisId)
        ? { ...prev, citation_count: normalizedCount, has_cited: hasCited }
        : prev
    ));
  };

  const handleCopyCitation = async (thesis) => {
    if (!thesis?.thesis_id) return;

    try {
      await copyTextToClipboard(getCitationText(thesis));
      setCopiedCitationId(thesis.thesis_id);

      if (!thesis.has_cited) {
        const res = await axios.post(`/api/theses/${thesis.thesis_id}/cite`, getCitationActor(currentUser));
        applyCitationUpdate(thesis.thesis_id, res.data?.citation_count, true);
      }

      window.setTimeout(() => {
        setCopiedCitationId((currentId) => (
          String(currentId) === String(thesis.thesis_id) ? null : currentId
        ));
      }, 2000);
    } catch (err) {
      if (err.response?.status === 409) {
        applyCitationUpdate(thesis.thesis_id, err.response.data?.citation_count, true);
        setCopiedCitationId(thesis.thesis_id);
        return;
      }

      console.error('Failed to copy citation:', err);
      alert('Failed to copy citation. Please try again.');
    }
  };

  const openViewModal = async (thesis) => {
    if (!thesis?.thesis_id) return;
    const latestThesis = theses.find((item) => String(item.thesis_id) === String(thesis.thesis_id)) || thesis;
    setViewThesis(latestThesis);
    try {
      const res = await axios.post(`/api/theses/${thesis.thesis_id}/view`);
      applyViewUpdate(thesis.thesis_id, res.data?.view_count);
    } catch (error) {
      console.error('Failed to record thesis view:', error);
    }
  };

  const openPdfModal = async (thesis) => {
    if (!thesis?.thesis_id) return;
    setPdfModalThesis(thesis);
    setPdfModalUrl('');

    if (!thesis.file_path) {
      return;
    }

    setIsPdfModalLoading(true);
    try {
      const url = await getSignedUrl(thesis.file_path);
      if (!url) {
        setPdfModalThesis(null);
        return;
      }
      setPdfModalUrl(url);
    } finally {
      setIsPdfModalLoading(false);
    }
  };

  const resolveEditDegreeMajor = (thesis) => {
    const dept = departments.find(d => d.dept_name === thesis?.department);
    let degreeName = '';
    let majorName = '';

    if (dept?.degrees?.length) {
      if (thesis?.degree_id) {
        const degreeObj = dept.degrees.find(d => d.degree_id === thesis.degree_id);
        degreeName = degreeObj?.degree_name || '';
        if (thesis?.major_id) {
          majorName = degreeObj?.majors?.find(m => m.major_id === thesis.major_id)?.major_name || '';
        }
      } else if (thesis?.major_id) {
        for (const degreeItem of dept.degrees) {
          const foundMajor = degreeItem.majors?.find(m => m.major_id === thesis.major_id);
          if (foundMajor) {
            degreeName = degreeItem.degree_name;
            majorName = foundMajor.major_name;
            break;
          }
        }
      }
    }

    return { degreeName, majorName, dept };
  };

  const openEditModalForThesis = (thesis) => {
    const { degreeName, majorName, dept } = resolveEditDegreeMajor(thesis);
    setEditFormData({
      ...thesis,
      degree: degreeName,
      major: majorName
    });
    setEditDegreesForDept(dept?.degrees || []);
    const selectedDegree = dept?.degrees?.find(d => d.degree_name === degreeName);
    setEditMajorsForDegree(selectedDegree?.majors || []);

    let convertedAuthors = [];
    if (thesis.author_data && Array.isArray(thesis.author_data)) {
      convertedAuthors = thesis.author_data.map(a => {
        if (typeof a === 'string') {
          return { ...parseAuthorName(a), email: '' };
        } else if (a.name) {
          return { ...parseAuthorName(a.name), email: a.email || '', author_id: a.author_id };
        }
        return a;
      });
    } else {
      convertedAuthors = (Array.isArray(thesis.authors) ? thesis.authors : []).map(a =>
        typeof a === 'string' ? { ...parseAuthorName(a), email: '' } : { ...a, email: a.email || '' }
      );
    }
    setEditAuthors(convertedAuthors.length > 0 ? convertedAuthors : [{ firstName: '', lastName: '', email: '' }]);
    setOriginalAuthorEmails(convertedAuthors.map(a => (a.email || '').trim()));
    setEditKeywords(thesis.keywords || []);
    setSelectedFile(null);
    setValidationResults(null);
    setShowConfirmation(false);
    setPendingEditData(null);
    setIsEditModalOpen(true);
  };

  const handleEditDepartmentChange = (departmentName) => {
    const dept = departments.find(d => d.dept_name === departmentName);
    const degrees = dept?.degrees || [];
    const defaultDegree = degrees.find(d => d.is_default) || degrees[0] || null;
    const majors = defaultDegree?.majors || [];
    const defaultMajor = majors.find(m => m.is_default) || majors[0] || null;

    setEditDegreesForDept(degrees);
    setEditMajorsForDegree(majors);
    setEditFormData(prev => ({
      ...prev,
      department: departmentName,
      degree: defaultDegree?.degree_name || '',
      major: defaultMajor?.major_name || ''
    }));
  };

  const handleEditDegreeChange = (degreeName) => {
    const degreeObj = editDegreesForDept.find(d => d.degree_name === degreeName);
    const majors = degreeObj?.majors || [];
    const defaultMajor = majors.find(m => m.is_default) || majors[0] || null;

    setEditMajorsForDegree(majors);
    setEditFormData(prev => ({
      ...prev,
      degree: degreeName,
      major: defaultMajor?.major_name || ''
    }));
  };

  // When department changes, reset degree and major filters
  useEffect(() => {
    setDegree('');
    setMajor('');
  }, [dept]);

  // When degree changes, reset major filter
  useEffect(() => {
    setMajor('');
  }, [degree]);

  const handleReset = () => {
      setKeyword('');
      setDept('');
      setDegree('');
      setMajor('');
      setSortField('modified');
      setSortDirection('desc');
  };

  useEffect(() => { fetchData(); }, [sortField, sortDirection, keyword, dept, degree, major]); // Auto-refresh on change
  useEffect(() => { setCurrentPage(1); }, [sortField, sortDirection, keyword, dept, degree, major]);
  useEffect(() => {
    const pageSize = 30;
    const totalPages = Math.max(1, Math.ceil(theses.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [theses.length, currentPage]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    const currentAuthorEmails = editAuthors.map(a => (a.email || '').trim());
    const normalizedOriginalEmails = originalAuthorEmails.map(email => (email || '').trim());
    const emailsChanged = !(
      currentAuthorEmails.length === normalizedOriginalEmails.length &&
      currentAuthorEmails.every((email, idx) => email === normalizedOriginalEmails[idx])
    );
    const hasAnyAuthorEmail = currentAuthorEmails.some(Boolean);

    if (!emailsChanged) {
      proceedWithUpdate();
      return;
    }

    if (!hasAnyAuthorEmail) {
      proceedWithUpdate();
      return;
    }

    // Validate emails only when at least one email is being added or changed
    setIsLoading(true);
    const authorStrings = editAuthors
      .filter(a => (a.firstName && a.firstName.trim() !== '') || (a.lastName && a.lastName.trim() !== ''))
      .map(a => {
        const first = (a.firstName || '').trim().toUpperCase();
        const last = (a.lastName || '').trim().toUpperCase();
        return `${first} ${last}`.trim();
      });

    try {
      const response = await axios.post('/api/validate-author-emails', {
        authors: authorStrings,
        emails: currentAuthorEmails
      });
      
      setValidationResults(response.data);
      setPendingEditData({}); // Store that we need to proceed
      setShowConfirmation(true);
    } catch (error) {
      alert("Error validating emails. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const proceedWithUpdate = async () => {
    const ok = await confirm({
      title: 'Save Changes',
      message: 'Are you sure you want to save these changes? This will overwrite the existing data and be recorded in the audit logs.',
      confirmText: 'Save',
      cancelText: 'Cancel'
    });
    
    if (!ok) return;

    const data = new FormData();
    // Append only the fields we need, excluding authors and keywords
    data.append('thesis_id', editFormData.thesis_id);
    data.append('title', editFormData.title.trim().toUpperCase());
    data.append('year', editFormData.year);
    data.append('department', editFormData.department);
    data.append('abstract', editFormData.abstract);
    
    // Add degree and major IDs if available
    const dept = departments.find(d => d.dept_name === editFormData.department);
    if (editFormData.degree) {
      const degreeObj = dept?.degrees?.find(d => d.degree_name === editFormData.degree);
      if (degreeObj) {
        data.append('degreeId', degreeObj.degree_id);
        if (editFormData.major) {
          const majorObj = degreeObj.majors?.find(m => m.major_name === editFormData.major);
          if (majorObj) {
            data.append('majorId', majorObj.major_id);
          }
        }
      }
    } else {
      data.append('degreeId', '');
    }
    
    if (selectedFile) data.append('thesisFile', selectedFile);
    
    // Convert author objects to clean name strings using || delimiter
    const authorStrings = editAuthors
      .filter(a => (a.firstName && a.firstName.trim() !== '') || (a.lastName && a.lastName.trim() !== ''))
      .map(a => {
        const first = (a.firstName || '').trim().replace(/[|,;]/g, '').toUpperCase(); // Remove special chars
        const last = (a.lastName || '').trim().replace(/[|,;]/g, '').toUpperCase(); // Remove special chars
        return `${first}||${last}`.trim();
      });
    const authorEmails = editAuthors.map(a => (a.email || '').trim());
    data.append('authors', JSON.stringify(authorStrings));
    data.append('authorEmails', JSON.stringify(authorEmails));
    data.append('keywords', JSON.stringify(editKeywords.map(k => k.trim()).filter(k => k)));
    data.append('userId', currentUser.user_id); 
    
    try {
      await axios.put(`/api/theses/${editFormData.thesis_id}`, data);
      
      setIsEditModalOpen(false);
      setShowConfirmation(false);
      setOriginalAuthorEmails([]);
      fetchData();
      alert("Changes saved successfully."); // Optional success feedback
    } catch (err) { 
      console.error(err);
      alert("Update failed. Please check the server connection."); 
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete Thesis',
      message: 'Delete permanently?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      danger: true
    });
    if (!ok) return;
    await axios.delete(`/api/theses/${id}?userId=${currentUser.user_id}`);
    fetchData();
  };

  const pageSize = 30;
  const deptForFilters = departments.find(d => d.dept_name === dept);
  const degreesForFilter = deptForFilters?.degrees || [];
  const majorsForFilter = degree
    ? (degreesForFilter.find(d => String(d.degree_id) === String(degree))?.majors || [])
    : [];
  const totalPages = Math.max(1, Math.ceil(theses.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, theses.length);
  const pagedTheses = theses.slice(startIndex, startIndex + pageSize);

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h2>Archive Management</h2>
        <button onClick={() => window.location.href = `${API_BASE}/api/backup/files`} style={backupBtnStyle}>Backup ZIP</button>
      </div>

      <div style={filterBarStyle}>
        <input type="text" placeholder="Search..." value={keyword} onChange={(e) => setKeyword(e.target.value)} style={searchInputStyle} />
        <select value={dept} onChange={(e) => setDept(e.target.value)} style={filterSelectStyle}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.dept_id} value={d.dept_name}>{d.dept_name}</option>)}
        </select>
        {dept && degreesForFilter.length > 0 && (
          <select value={degree} onChange={(e) => setDegree(e.target.value)} style={filterSelectStyle}>
            <option value="">All Degrees</option>
            {degreesForFilter.map(d => (
              <option key={d.degree_id} value={d.degree_id}>{d.degree_name}</option>
            ))}
          </select>
        )}
        {degree && majorsForFilter.length > 0 && (
          <select value={major} onChange={(e) => setMajor(e.target.value)} style={filterSelectStyle}>
            <option value="">All Majors</option>
            {majorsForFilter.map(m => (
              <option key={m.major_id} value={m.major_id}>{m.major_name}</option>
            ))}
          </select>
        )}
        <select value={sortField} onChange={(e) => setSortField(e.target.value)} style={filterSelectStyle}>
            <option value="modified">Last Modified</option>
            <option value="uploaded">Last Added</option>
            <option value="title">Title</option>
            <option value="year">Year</option>
            <option value="view">Views</option>
            <option value="citation">Citations</option>
        </select>
        <button
          type="button"
          onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          style={sortToggleBtn}
        >
          {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
        </button>
        <button onClick={handleReset} style={filterResetStyle}>Reset Filters</button>
      </div>

      <div style={{ color: '#666', marginBottom: '10px' }}>
        Total: {theses.length} thesis(es)
        {theses.length > 0 ? ` • Showing ${startIndex + 1}-${endIndex}` : ''}
      </div>

      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={tTh}>Thesis Title</th>
              <th style={tTh}>Dept</th>
              <th style={tTh}>Views</th>
              <th style={tTh}>File</th>
              <th style={tTh}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedTheses.map(t => {
                const dept = departments.find(d => d.dept_name === t.department);
                let degreeName = '';
                let majorName = '';
                if (dept?.degrees?.length) {
                  if (t.degree_id) {
                    const degreeObj = dept.degrees.find(d => d.degree_id === t.degree_id);
                    degreeName = degreeObj?.degree_name || '';
                    if (t.major_id) {
                      majorName = degreeObj?.majors?.find(m => m.major_id === t.major_id)?.major_name || '';
                    }
                  } else if (t.major_id) {
                    for (const degree of dept.degrees) {
                      const foundMajor = degree.majors?.find(m => m.major_id === t.major_id);
                      if (foundMajor) {
                        degreeName = degree.degree_name;
                        majorName = foundMajor.major_name;
                        break;
                      }
                    }
                  }
                }
                const deptDisplay = [t.department, degreeName, majorName].filter(Boolean).join(' - ');
                return (
                  <tr key={t.thesis_id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={titleCell}><strong>{t.title}</strong></td>
                <td style={deptCell}>{deptDisplay}</td>
                <td style={viewsCell}>{Number(t.view_count || 0)}</td>
                <td style={fileCell}>
                  <div style={fileActionStack}>
                    {t.file_path ? (
                      <>
                        <button 
                          onClick={() => openPdfModal(t)} 
                          style={fileActionPrimaryBtn}
                        >
                          View PDF
                        </button>
                        <button onClick={() => handleDownload(t.file_path)} style={fileActionSecondaryBtn}>
                          Download PDF
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => openPdfModal(t)} 
                          style={fileActionPrimaryBtn}
                        >
                          Upload PDF
                        </button>
                        <span style={missingPdfTextStyle}>PDF file missing</span>
                      </>
                    )}
                  </div>
                </td>
                <td style={actionCell}>
                  <button onClick={() => openViewModal(t)} style={vBtn}>View</button>
                  <button onClick={() => { 
                    openEditModalForThesis(t);
                  }} style={eBtn}>Edit</button>
                  {currentUser.role === 'admin' && <button onClick={() => handleDelete(t.thesis_id)} style={dBtn}>Delete</button>}
                </td>
              </tr>
            );  
            })} 
          </tbody>
        </table>
      </div>
      {theses.length > 0 && totalPages > 1 && (
        <div style={paginationBar}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{ ...pageBtn, opacity: currentPage === 1 ? 0.6 : 1 }}
          >
            Previous
          </button>
          <span style={pageInfo}>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{ ...pageBtn, opacity: currentPage === totalPages ? 0.6 : 1 }}
          >
            Next
          </button>
        </div>
      )}

      {/* VIEW MODAL */}
        {viewThesis && (
        <div style={modalOverlay}>
          <div style={{ ...modalContent, display: 'flex', flexDirection: 'column' }}>
            <h3>{viewThesis.title}</h3>
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
              <p style={{ color: '#666', fontSize: '13px', marginTop: 0, marginBottom: '15px' }}>
                Views: {Number(viewThesis.view_count || 0)} | Citations: {Number(viewThesis.citation_count || 0)}
              </p>
              {(() => {
                const dept = departments.find(d => d.dept_name === viewThesis.department);
                let degreeName = '';
                let majorName = '';
                if (dept?.degrees?.length) {
                  if (viewThesis.degree_id) {
                    const degreeObj = dept.degrees.find(d => d.degree_id === viewThesis.degree_id);
                    degreeName = degreeObj?.degree_name || '';
                    if (viewThesis.major_id) {
                      majorName = degreeObj?.majors?.find(m => m.major_id === viewThesis.major_id)?.major_name || '';
                    }
                  } else if (viewThesis.major_id) {
                    for (const degree of dept.degrees) {
                      const foundMajor = degree.majors?.find(m => m.major_id === viewThesis.major_id);
                      if (foundMajor) {
                        degreeName = degree.degree_name;
                        majorName = foundMajor.major_name;
                        break;
                      }
                    }
                  }
                }
                const deptDisplay = [viewThesis.department, degreeName, majorName].filter(Boolean).join(' - ');
                return <p style={metaText}>{viewThesis.year} | {deptDisplay}</p>;
              })()}
              {viewThesis.authors && viewThesis.authors.length > 0 && (
                <p style={{ margin: '6px 0' }}><strong>Authors:</strong> {formatAuthorsForDisplay(viewThesis.authors)}</p>
              )}
              {viewThesis.keywords && viewThesis.keywords.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {viewThesis.keywords.map((k, i) => (
                    <span key={i} style={{ background: '#eef', padding: '6px 8px', borderRadius: '12px', fontSize: '13px' }}>{k}</span>
                  ))}
                </div>
              )}
              <div style={abstractScroll}>{viewThesis.abstract}</div>
              <div style={citationPanelStyle}>
                <div style={citationHeaderRow}>
                  <h4 style={{ margin: 0, color: '#333' }}>Citation</h4>
                </div>
                <p style={citationTextStyle}>{getCitationText(viewThesis)}</p>
                <div style={citationFooterRow}>
                  <span style={citationCountText}>Citation count: {Number(viewThesis.citation_count || 0)}</span>
                  <button
                    type="button"
                    onClick={() => handleCopyCitation(viewThesis)}
                    style={copyCitationBtn}
                  >
                    {String(copiedCitationId) === String(viewThesis.thesis_id) ? 'Copied' : 'Copy Citation'}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'20px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewThesis(null)} style={closeBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      {pdfModalThesis && (
        <div style={modalOverlay} onClick={closePdfModal}>
          <div
            style={{
              ...modalContent,
              width: pdfModalThesis.file_path ? '95%' : '560px',
              height: pdfModalThesis.file_path ? '90vh' : undefined,
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>{pdfModalThesis.file_path ? 'Read PDF' : 'Attach PDF'}</h3>
            <p style={{ ...metaText, marginTop: 0, marginBottom: '14px' }}>{pdfModalThesis.title}</p>

            {pdfModalThesis.file_path ? (
              isPdfModalLoading ? (
                <div style={pdfViewerLoading}>
                  <div style={spinnerStyle}></div>
                  <p style={{ margin: '12px 0 0' }}>Loading PDF...</p>
                </div>
              ) : (
                <div style={pdfViewerShell}>
                  <iframe title="PDF" src={`${pdfModalUrl}#toolbar=0`} width="100%" height="100%" style={{ border: 'none' }} />
                </div>
              )
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                <div style={uploadNoticeStyle}>
                  This thesis does not have a PDF yet. Uploading one will attach it to this thesis record and make it available in the archive.
                </div>
                <label style={{ ...labelStyle, marginTop: '14px' }}>Select PDF</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleMissingPdfFileChange(pdfModalThesis.thesis_id, e.target.files[0])}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {missingPdfFiles[pdfModalThesis.thesis_id]?.name || 'No file selected'}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
              {pdfModalThesis.file_path ? (
                <button onClick={() => handleDownload(pdfModalThesis.file_path)} style={downloadBtn}>
                  Download
                </button>
              ) : (
                <button
                  onClick={() => uploadMissingPdf(pdfModalThesis)}
                  style={{ ...saveBtn, flex: 1, opacity: isPdfUploading ? 0.7 : 1 }}
                  disabled={isPdfUploading}
                >
                  {isPdfUploading ? 'Uploading...' : 'Upload PDF'}
                </button>
              )}
              <button onClick={closePdfModal} style={closeBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {isEditModalOpen && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            
            {/* LOADING OVERLAY */}
            {isLoading && (
              <div style={loadingInnerOverlay}>
                <div style={spinnerStyle}></div>
                <p>Validating author emails...</p>
              </div>
            )}

            <h3>Edit Thesis</h3>
            <form onSubmit={handleUpdate}>
              {/* Title */}
              <label style={labelStyle}>Title</label>
              <input 
                type="text" 
                value={editFormData.title} 
                onChange={e => setEditFormData({...editFormData, title: e.target.value.toUpperCase()})} 
                style={modalInput} 
              />

              {/* Year & Department Row */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Year</label>
                  <input 
                    type="number" 
                    value={editFormData.year} 
                    onChange={e => setEditFormData({...editFormData, year: e.target.value})} 
                    style={modalInput} 
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Department</label>
                  <select 
                    value={editFormData.department} 
                    onChange={e => handleEditDepartmentChange(e.target.value)} 
                    style={modalInput}
                  >
                    <option value="">Select Dept</option>
                    {departments.map(d => <option key={d.dept_id} value={d.dept_name}>{d.dept_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Degree (if available) */}
              {editDegreesForDept.length > 0 && (
                <div>
                  <label style={labelStyle}>Degree</label>
                  <select 
                    value={editFormData.degree} 
                    onChange={e => handleEditDegreeChange(e.target.value)} 
                    style={modalInput}
                  >
                    <option value="">Select Degree</option>
                    {editDegreesForDept.map(degree => (
                      <option key={degree.degree_id} value={degree.degree_name}>{degree.degree_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Major (if available) */}
              {editMajorsForDegree.length > 0 && (
                <div>
                  <label style={labelStyle}>Major</label>
                  <select 
                    value={editFormData.major} 
                    onChange={e => setEditFormData({...editFormData, major: e.target.value})} 
                    style={modalInput}
                  >
                    <option value="">Select Major</option>
                    {editMajorsForDegree.map(major => (
                      <option key={major.major_id} value={major.major_name}>{major.major_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Abstract */}
              <label style={labelStyle}>Abstract</label>
              <textarea 
                value={editFormData.abstract} 
                onChange={e => setEditFormData({...editFormData, abstract: e.target.value})} 
                style={{...modalInput, height: '100px'}} 
              />

              {/* Authors Editor */}
              <label style={labelStyle}>Authors</label>
              <p style={{ fontSize: '12px', color: '#999', marginBottom: '10px', fontStyle: 'italic' }}>
                💡 Optional: Add author email addresses for readers to request PDFs directly from the authors.
              </p>
              {editAuthors.map((a, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <input 
                      placeholder="First Name" 
                      value={a.firstName || ''} 
                      onChange={e => { const copy = [...editAuthors]; copy[idx].firstName = e.target.value.toUpperCase(); setEditAuthors(copy); }} 
                      style={{ ...modalInput, marginBottom: '4px' }} 
                    />
                    <input 
                      placeholder="Last Name" 
                      value={a.lastName || ''} 
                      onChange={e => { const copy = [...editAuthors]; copy[idx].lastName = e.target.value.toUpperCase(); setEditAuthors(copy); }} 
                      style={{ ...modalInput, marginBottom: '4px' }} 
                    />
                    <input 
                      placeholder="Email (Optional)" 
                      type="email"
                      value={a.email || ''}
                      onChange={e => { const copy = [...editAuthors]; copy[idx].email = e.target.value; setEditAuthors(copy); }} 
                      style={modalInput}
                    />
                  </div>
                  <button type="button" onClick={() => { const copy = [...editAuthors]; copy.splice(idx, 1); setEditAuthors(copy); }} style={{ padding: '6px 10px', height: 'fit-content', marginTop: '4px' }}>Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setEditAuthors([...editAuthors, { firstName: '', lastName: '', email: '' }])} style={{ marginBottom: '10px' }}>Add Author</button>

              {/* Keywords Editor */}
              <label style={labelStyle}>Keywords</label>
              <KeywordInput keywords={editKeywords} setKeywords={setEditKeywords} />

              <label style={{fontSize:'12px', display:'block', marginBottom:'5px'}}>Replace PDF (Optional):</label>
              <input type="file" accept=".pdf" onChange={e => setSelectedFile(e.target.files[0])} style={{marginBottom:'6px'}} />
        
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setIsEditModalOpen(false)} style={closeBtn}>Cancel</button>
                <button type="submit" style={saveBtn}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL FOR AUTHOR ACCOUNTS */}
      {showConfirmation && validationResults && (
        <div style={modalOverlay} onClick={() => setShowConfirmation(false)}>
          <div style={{ ...modalContent, width: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Confirm Author Email Changes</h3>
            
            <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '15px' }}>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '15px' }}>
                The following author email changes will be saved:
              </p>

              {validationResults.map((result, idx) => (
                <div key={idx} style={resultBoxStyle(result.status)}>
                  <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{result.name}</p>
                  <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#666' }}>
                    Email: {result.email}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontStyle: 'italic' }}>
                    {result.status === 'new' && 'Email will be saved for author contact requests'}
                    {result.status === 'existing' && `Email already belongs to author: ${result.existingName}`}
                    {result.status === 'no_email' && '- No email provided'}
                  </p>
                </div>
              ))}
            </div>
            <p style={{ background: '#e7f3ff', padding: '12px', borderRadius: '4px', fontSize: '0.85rem', color: '#004085', margin: '15px 0' }}>
              <strong>Note:</strong> No user account will be created. Guest PDF requests will simply email the listed authors directly.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={proceedWithUpdate}
                style={{ ...saveBtn, flex: 1 }}
              >
                Confirm & Save
              </button>
              <button 
                onClick={() => setShowConfirmation(false)}
                style={{ ...closeBtn, flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal state={confirmState} onClose={handleClose} />
    </div>
  );
};

// --- STYLES ---
const filterBarStyle = { display: 'flex', gap: '10px', marginBottom: '20px', background: '#f4f4f4', padding: '15px', borderRadius: '8px', flexWrap: 'wrap' };
const mInput = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: 0, boxSizing: 'border-box' };
const searchInputStyle = { ...mInput, flex: '2 1 240px' };
const filterSelectStyle = { ...mInput, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' };
const sortToggleBtn = { padding: '8px 14px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontWeight: 'bold' };
const tableContainer = { background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflowX: 'auto' };
const tableStyle = { width: '100%', minWidth: '840px', borderCollapse: 'collapse' };
const tTh = { padding: '12px', textAlign: 'left', fontSize: '14px' };
const tTd = { padding: '12px', fontSize: '14px' };
const titleCell = { ...tTd, minWidth: '260px' };
const deptCell = { ...tTd, minWidth: '220px' };
const viewsCell = { ...tTd, minWidth: '80px', textAlign: 'center', fontWeight: 'bold' };
const fileCell = { ...tTd, minWidth: '170px' };
const actionCell = { ...tTd, whiteSpace: 'nowrap', minWidth: '180px' };
const pdfLink = { color:'#28a745', textDecoration:'none', fontWeight:'bold' };
const fileActionStack = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' };
const fileActionPrimaryBtn = { padding: '8px 12px', background: '#800000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const fileActionSecondaryBtn = { padding: '8px 12px', background: '#e8f5ec', color: '#1b5e20', border: '1px solid #28a745', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const missingPdfTextStyle = { color: '#856404', fontSize: '0.9rem', fontWeight: 'bold' };
const vBtn = { background: '#f0f0f0', border: '1px solid #ccc', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', marginRight:'5px' };
const eBtn = { background: '#e7f1ff', color: '#007bff', border: '1px solid #007bff', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', marginRight:'5px' };
const dBtn = { background: '#fff5f5', color: '#dc3545', border: '1px solid #dc3545', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' };
const backupBtnStyle = { padding: '8px 15px', background: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const resetStyle = { padding: '8px 15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const filterResetStyle = { ...resetStyle, flex: '0 1 auto' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { background: 'white', padding: '20px', borderRadius: '8px', width: '500px', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box' };
const modalInput = { width: '100%', padding: '10px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' };
const abstractScroll = { background:'#f9f9f9', padding:'15px', borderRadius:'4px', fontSize:'13px', maxHeight:'200px', overflowY:'auto', border:'1px solid #eee' };
const citationPanelStyle = { marginTop: '14px', padding: '14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafafa' };
const citationHeaderRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' };
const citationTextStyle = { fontSize: '0.92rem', color: '#4b5563', lineHeight: '1.6', margin: '0 0 12px 0' };
const citationFooterRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
const citationCountText = { color: '#666', fontSize: '0.9rem', fontWeight: 'bold' };
const copyCitationBtn = { padding: '9px 14px', background: '#800000', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const metaText = { color:'#666', fontSize:'13px', marginBottom:'15px' };
const readBtn = { flex:1, padding:'10px', textAlign:'center', background:'#800000', color:'white', borderRadius:'4px', textDecoration:'none' };
const downloadBtn = { flex:1, padding:'10px', textAlign:'center', background:'#28a745', color:'white', borderRadius:'4px', textDecoration:'none' };
const closeBtn = { padding:'8px 15px', border:'1px solid #ddd', borderRadius:'4px', cursor:'pointer' };
const saveBtn = { padding: '8px 15px', background: '#800000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const labelStyle = { fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' };
const uploadNoticeStyle = { padding: '12px 14px', background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '6px', color: '#856404', fontSize: '0.9rem' };
const pdfViewerShell = { flex: 1, background: '#525659', borderRadius: '8px', overflow: 'hidden', minHeight: '320px' };
const pdfViewerLoading = { ...pdfViewerShell, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' };

const loadingInnerOverlay = { position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' };
const spinnerStyle = { border: '4px solid #f3f3f3', borderTop: '4px solid #800000', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', marginBottom: '15px' };
const paginationBar = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' };
const pageBtn = { padding: '8px 14px', border: '1px solid #ddd', background: '#fff', borderRadius: '6px', cursor: 'pointer' };
const pageInfo = { color: '#666', fontSize: '0.9rem' };

const resultBoxStyle = (status) => {
    const baseStyle = { padding: '12px', borderRadius: '4px', marginBottom: '10px', borderLeft: '4px solid #ddd' };
    switch(status) {
        case 'new': return { ...baseStyle, background: '#d4edda', borderLeft: '4px solid #28a745', color: '#155724' };
        case 'existing': return { ...baseStyle, background: '#fff3cd', borderLeft: '4px solid #ffc107', color: '#856404' };
        case 'no_email': return { ...baseStyle, background: '#e2e3e5', borderLeft: '4px solid #6c757d', color: '#383d41' };
        default: return baseStyle;
    }
};

const KeywordInput = ({ keywords, setKeywords }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const val = input.trim();
    if (!val) return;
    if (!keywords.includes(val)) setKeywords([...keywords, val]);
    setInput('');
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="Add keyword and press Enter" style={modalInput} />
        <button type="button" onClick={add} style={{ padding: '8px 12px' }}>Add</button>
      </div>
      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {keywords.map((k, i) => (
          <div key={i} style={{ background: '#eef', padding: '6px 8px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px' }}>{k}</span>
            <button type="button" onClick={() => setKeywords(keywords.filter((_, idx) => idx !== i))} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThesisList;
